import { Suspense, useEffect, useMemo } from 'react';
import { useGLTF, Clone } from '@react-three/drei';
import { RigidBody, CuboidCollider } from '@react-three/rapier';
import { useWorldBuilderStore } from '../../../store/worldBuilderStore';
import { assetUrl } from '../../../assets/thornhavenAssetRegistry';
import { NATIVE_ASSET_MAP, snapTransform } from '../environment/prefabs/nativeRegistry';

function GltfModel({ url, scale }) {
  const { scene } = useGLTF(url);
  return <Clone object={scene} scale={scale} />;
}

function NativeModel({ assetId, scale }) {
  const obj = useMemo(() => {
    const entry = NATIVE_ASSET_MAP[assetId];
    return entry && entry.build ? entry.build() : null;
  }, [assetId]);
  if (!obj) return null;
  return <primitive object={obj} scale={scale} />;
}

function Model({ assetId, scale }) {
  if (NATIVE_ASSET_MAP[assetId]) return <NativeModel assetId={assetId} scale={scale} />;
  const url = assetUrl(assetId);
  if (!url) return null;
  return <GltfModel url={url} scale={scale} />;
}

// Decorative native assets (grass, flowers, small props) get no player collision.
function collisionFor(assetId) {
  const entry = NATIVE_ASSET_MAP[assetId];
  if (entry) return entry.collision; // 'none' | 'cuboid' | 'ramp' | 'gate'
  return 'cuboid'; // plugin assets default to solid
}

// One inclined collider spanning ground->platform so ramps and stairs are smooth
// walkable slopes instead of floating slabs / un-climbable 0.3m steps.
function SlopeCollider({ slope, scale }) {
  const { w, low, high, t } = slope;
  const dz = (high[0] - low[0]) * scale;
  const dy = (high[1] - low[1]) * scale;
  const len = Math.hypot(dz, dy) || 0.01;
  const angle = Math.atan2(-dy, dz); // rotate about X so local +z follows the slope
  const cz = ((low[0] + high[0]) / 2) * scale;
  const cy = ((low[1] + high[1]) / 2) * scale;
  const th = t * scale;
  return (
    <CuboidCollider
      args={[(w * scale) / 2, th / 2, len / 2]}
      position={[0, cy, cz]}
      rotation={[angle, 0, 0]}
    />
  );
}

// Only the two side posts collide; the central opening stays passable.
function GateColliders({ gate, scale }) {
  const hw = (gate.postW * scale) / 2;
  const hh = (gate.h * scale) / 2;
  const hd = 0.3 * scale;
  return (
    <>
      <CuboidCollider args={[hw, hh, hd]} position={[gate.half * scale, hh, 0]} />
      <CuboidCollider args={[hw, hh, hd]} position={[-gate.half * scale, hh, 0]} />
    </>
  );
}

function SavedObject({ obj, editing, draft, onSelect }) {
  const position = editing ? draft.position : obj.position;
  const rotationY = editing ? draft.rotation_y : obj.rotation_y || 0;
  const scale = editing ? draft.scale : obj.scale || 1;
  const handleSelect = (e) => {
    e.stopPropagation();
    onSelect(obj.id);
  };

  const content = (
    <group onClick={handleSelect}>
      <Suspense fallback={null}>
        <Model assetId={obj.asset_id} scale={scale} />
      </Suspense>
    </group>
  );

  const col = collisionFor(obj.asset_id);

  if (editing || col === 'none') {
    return (
      <group position={position} rotation={[0, rotationY, 0]} onClick={handleSelect}>
        <Suspense fallback={null}>
          <Model assetId={obj.asset_id} scale={scale} />
        </Suspense>
      </group>
    );
  }

  const meta = NATIVE_ASSET_MAP[obj.asset_id];
  const custom = col === 'ramp' || col === 'gate';

  return (
    <RigidBody
      key={`${obj.id}-${position.join(',')}-${rotationY}-${scale}`}
      type="fixed"
      colliders={custom ? false : 'cuboid'}
      position={position}
      rotation={[0, rotationY, 0]}
    >
      {content}
      {col === 'ramp' && meta?.slope && <SlopeCollider slope={meta.slope} scale={scale} />}
      {col === 'gate' && meta?.gate && <GateColliders gate={meta.gate} scale={scale} />}
    </RigidBody>
  );
}

function GltfGhost({ assetId, position }) {
  const url = assetUrl(assetId);
  const { scene } = useGLTF(url);
  const ghost = useMemo(() => {
    const clone = scene.clone(true);
    clone.traverse((o) => {
      if (o.isMesh && o.material) {
        o.material = Array.isArray(o.material) ? o.material.map((m) => m.clone()) : o.material.clone();
        const apply = (m) => {
          m.transparent = true;
          m.opacity = 0.55;
          m.depthWrite = false;
        };
        if (Array.isArray(o.material)) o.material.forEach(apply);
        else apply(o.material);
      }
    });
    return clone;
  }, [scene]);
  return <primitive object={ghost} position={position} />;
}

function GhostPreview({ assetId, position }) {
  const nativeObj = useMemo(() => {
    const entry = NATIVE_ASSET_MAP[assetId];
    return entry && entry.build ? entry.build() : null;
  }, [assetId]);
  if (NATIVE_ASSET_MAP[assetId]) {
    return nativeObj ? <primitive object={nativeObj} position={position} /> : null;
  }
  return <GltfGhost assetId={assetId} position={position} />;
}

function PlacementHelper() {
  const builderEnabled = useWorldBuilderStore((s) => s.builderEnabled);
  const selectedAssetId = useWorldBuilderStore((s) => s.selectedAssetId);
  const moveMode = useWorldBuilderStore((s) => s.moveMode);
  const ghostPosition = useWorldBuilderStore((s) => s.ghostPosition);
  const setGhost = useWorldBuilderStore((s) => s.setGhost);
  const placeAt = useWorldBuilderStore((s) => s.placeAt);
  const moveSelectedTo = useWorldBuilderStore((s) => s.moveSelectedTo);

  const active = builderEnabled && (!!selectedAssetId || moveMode);
  if (!active) return null;

  const onMove = (e) => {
    e.stopPropagation();
    if (selectedAssetId && !moveMode) {
      const t = snapTransform(selectedAssetId, e.point.x, e.point.z);
      setGhost(t.position);
    } else {
      setGhost([e.point.x, 0, e.point.z]);
    }
  };
  const onDown = (e) => {
    e.stopPropagation();
    const p = [e.point.x, 0, e.point.z];
    if (moveMode) moveSelectedTo(p);
    else if (selectedAssetId) placeAt(p);
  };

  return (
    <group>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.02, 0]} onPointerMove={onMove} onPointerDown={onDown}>
        <planeGeometry args={[600, 600]} />
        <meshBasicMaterial transparent opacity={0} depthWrite={false} />
      </mesh>
      {selectedAssetId && !moveMode && (
        <Suspense fallback={null}>
          <GhostPreview assetId={selectedAssetId} position={ghostPosition} />
        </Suspense>
      )}
    </group>
  );
}

export default function WorldObjectsLayer() {
  const objects = useWorldBuilderStore((s) => s.objects);
  const loaded = useWorldBuilderStore((s) => s.loaded);
  const fetchObjects = useWorldBuilderStore((s) => s.fetchObjects);
  const selectedObjectId = useWorldBuilderStore((s) => s.selectedObjectId);
  const draft = useWorldBuilderStore((s) => s.draft);
  const selectObject = useWorldBuilderStore((s) => s.selectObject);
  const builderEnabled = useWorldBuilderStore((s) => s.builderEnabled);

  useEffect(() => {
    if (!loaded) fetchObjects();
  }, [loaded, fetchObjects]);

  return (
    <group name="world-builder-objects">
      {objects.map((obj) => (
        <SavedObject
          key={obj.id}
          obj={obj}
          editing={builderEnabled && obj.id === selectedObjectId}
          draft={draft}
          onSelect={builderEnabled ? selectObject : () => {}}
        />
      ))}
      <PlacementHelper />
    </group>
  );
}
